/**
 * TrialStatus — surfaces free-trial usage for the resolved tenant.
 *
 * Visual contract (per Trackerity brand):
 *   - emerald → sky gradient heading
 *   - 6–8px rounded corners, zinc borders, Inter font
 *   - progress bar: emerald fill on a sky "track"
 *   - surfaces an "Upgrade" CTA once the tenant hits the near-cap threshold
 *
 * Data pulled via TanStack Query v5 (see `./api.ts`).
 */

import {
	deriveTrialView,
	useFreeTrialStatus,
	type FreeTrialSnapshot,
} from "./api";

function formatNumber(n: number): string {
	return new Intl.NumberFormat("en-US").format(n);
}

function formatExpiresAt(iso: string | null): string {
	if (!iso) return "—";
	try {
		return new Intl.DateTimeFormat("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		}).format(new Date(iso));
	} catch {
		return iso;
	}
}

function Card({ children }: { children: React.ReactNode }) {
	return (
		<div
			className="w-full max-w-2xl rounded-[8px] border border-zinc-200 bg-white p-8 shadow-sm"
			style={{
				boxShadow:
					"0 1px 3px 0 rgb(24 24 27 / 0.08), 0 4px 8px -2px rgb(24 24 27 / 0.06)",
			}}
		>
			{children}
		</div>
	);
}

function GradientHeading({ children }: { children: React.ReactNode }) {
	return (
		<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
			{children}
		</h1>
	);
}

function ProgressBar({
	percent,
	used,
	cap,
}: {
	percent: number;
	used: number;
	cap: number;
}) {
	const pctRounded = Math.round(percent * 10) / 10;
	return (
		<div className="w-full">
			<div className="flex items-baseline justify-between pb-2 text-sm">
				<span className="font-medium text-zinc-700">Conversions used</span>
				<span className="text-zinc-500">
					{formatNumber(used)} / {formatNumber(cap)} ({pctRounded}%)
				</span>
			</div>
			<div
				role="progressbar"
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={pctRounded}
				aria-label="Free trial conversions used"
				className="relative h-3 w-full overflow-hidden rounded-[6px] bg-sky-100"
			>
				<div
					className="h-full rounded-[6px] bg-emerald-500 transition-[width] duration-500 ease-out"
					style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
				/>
			</div>
		</div>
	);
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
	return (
		<div className="flex flex-col gap-1 rounded-[6px] border border-zinc-200 bg-zinc-50 p-4">
			<span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
				{label}
			</span>
			<span className="text-base font-semibold text-zinc-900">{value}</span>
		</div>
	);
}

function UpgradeCta({ reason }: { reason: string }) {
	return (
		<div className="flex flex-col gap-3 rounded-[8px] border border-emerald-200 bg-gradient-to-r from-emerald-50 to-sky-50 p-5 sm:flex-row sm:items-center sm:justify-between">
			<div className="flex flex-col gap-1">
				<span className="text-sm font-semibold text-zinc-900">
					Upgrade to keep tracking
				</span>
				<span className="text-xs text-zinc-600">{reason}</span>
			</div>
			<a
				href="/billing/upgrade"
				className="inline-flex shrink-0 items-center justify-center rounded-[6px] bg-gradient-to-r from-emerald-500 to-sky-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2"
			>
				Upgrade
			</a>
		</div>
	);
}

function SkeletonState() {
	return (
		<Card>
			<GradientHeading>Free trial</GradientHeading>
			<p className="mt-2 text-sm text-zinc-500">Loading trial status...</p>
			<div className="mt-6 h-3 w-full animate-pulse rounded-[6px] bg-zinc-100" />
			<div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
				<div className="h-20 animate-pulse rounded-[6px] bg-zinc-50" />
				<div className="h-20 animate-pulse rounded-[6px] bg-zinc-50" />
				<div className="h-20 animate-pulse rounded-[6px] bg-zinc-50" />
			</div>
		</Card>
	);
}

function ErrorState({
	message,
	onRetry,
}: {
	message: string;
	onRetry: () => void;
}) {
	return (
		<Card>
			<GradientHeading>Free trial</GradientHeading>
			<p className="mt-2 text-sm text-zinc-500">
				Couldn&apos;t load your trial status.
			</p>
			<div className="mt-6 rounded-[6px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700">
				{message}
			</div>
			<button
				type="button"
				onClick={onRetry}
				className="mt-4 inline-flex items-center rounded-[6px] border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-800 hover:bg-zinc-50"
			>
				Retry
			</button>
		</Card>
	);
}

function NotStartedState({ snapshot }: { snapshot: FreeTrialSnapshot }) {
	if (snapshot.exists) return null;
	return (
		<Card>
			<GradientHeading>Free trial</GradientHeading>
			<p className="mt-2 text-sm text-zinc-500">
				Your workspace hasn&apos;t opted in yet. Every new workspace gets{" "}
				{formatNumber(snapshot.cap)} conversions across{" "}
				{snapshot.window_days} days — no card required.
			</p>
			<div className="mt-6 flex">
				<a
					href="/billing/start-trial"
					className="inline-flex items-center justify-center rounded-[6px] bg-gradient-to-r from-emerald-500 to-sky-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-95"
				>
					Start free trial
				</a>
			</div>
		</Card>
	);
}

export function TrialStatus() {
	const query = useFreeTrialStatus();

	if (query.isLoading) {
		return <SkeletonState />;
	}

	if (query.isError || !query.data) {
		return (
			<ErrorState
				message={
					query.error instanceof Error
						? query.error.message
						: "Unknown error while fetching /free-trial/status"
				}
				onRetry={() => void query.refetch()}
			/>
		);
	}

	const snapshot = query.data;

	if (!snapshot.exists) {
		return <NotStartedState snapshot={snapshot} />;
	}

	const derived = deriveTrialView(snapshot);
	const used = snapshot.conversion_count;
	const upgradeReason =
		snapshot.status === "expired_count"
			? "You've used every conversion in the trial. Upgrade to keep events flowing."
			: snapshot.status === "expired_time"
				? "Your 30-day window has elapsed. Upgrade to reactivate tracking."
				: derived.daysRemaining <= 3
					? `Only ${derived.daysRemaining} day${derived.daysRemaining === 1 ? "" : "s"} left in your trial window.`
					: `You're at ${Math.round(derived.percentUsed)}% of your conversion cap.`;

	return (
		<Card>
			<div className="flex flex-col gap-1">
				<GradientHeading>Free trial</GradientHeading>
				<p className="text-sm text-zinc-500">
					{formatNumber(snapshot.cap)} conversions across{" "}
					{snapshot.window_days} days. Cap or window, whichever lands first.
				</p>
			</div>

			<div className="mt-8">
				<ProgressBar percent={derived.percentUsed} used={used} cap={snapshot.cap} />
			</div>

			<div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
				<Stat
					label="Days remaining"
					value={`${derived.daysRemaining} / ${snapshot.window_days}`}
				/>
				<Stat label="Status" value={derived.statusLabel} />
				<Stat label="Expires" value={formatExpiresAt(derived.expiresAtIso)} />
			</div>

			{derived.nearCap ? (
				<div className="mt-6">
					<UpgradeCta reason={upgradeReason} />
				</div>
			) : null}
		</Card>
	);
}
